<template>
  <div v-if="loaded" class="modal__wrapper delegate-modal" :class="{ 'modal-open': modalVisible }" :id="wrapperId" ref="modalwrapper">
    <div class="modal" ref="modal">
      <h2 class="modal__title" tabindex="-1">
        Delegate Login
      </h2>
      <div class="modal__body">
        <div class="body-content__wrapper">
          <div class="container--narrow">
            <div class="body-content wysiwyg" :id="bodyContentWrapper" v-html="modalTextIntro"></div>
          </div>
        </div>
        <form @submit.prevent="submitForm" id="delegate-login-form" class="delegate-login-form" aria-live="polite" :aria-busy="ariaBusy">
          <div class="delegate-login-form__input form-item">
            <label for="delegate-login-email" class="form-required">Email</label>
            <input v-model="email" type="email" required name="delegate-login-email" id="delegate-login-email" />
            <div class="form-help">Enter the same email address used for your NEA RA registration on Cvent</div>
          </div>
          <div class="delegate-login-form__input form-item">
            <label :for="delegateLoginId" class="form-required">2024 NEA Confirmation Number</label>
            <input v-model="id" type="text" required minlength="10" :name="delegateLoginId" :id="delegateLoginId" />
            <div class="form-help">Enter the confirmation number you received in your registration confirmation email</div>
          </div>
          <div class="alerts">
            <div v-if="authFailed" id="delegate-login-form-error" class="alert alert--error" role="alert">{{ failureMessage }}</div>
            <div v-if="authSucceeded" id="delegate-login-form-success" class="alert alert--success" role="alert">{{ successMessage }}</div>
          </div>
          <div class="delegate-login-form__submit form-item">
            <input id="delegate-login-form-submit" type="submit" value="submit" class="btn btn--secondary btn--no-arrow" />
            <div class="loading-indicator">
              <svg width="32" height="32">
                <circle cx="50%" cy="50%" r="14" fill="none" stroke-width="4" class="loading-indicator__forground"></circle>
                <circle cx="50%" cy="50%" r="14" fill="none" stroke-width="4" class="loading-indicator__background"></circle>
              </svg>
            </div>
          </div>
        </form>
        <div class="delegate-login-modal__footer">
          <div class="body-content__wrapper">
            <div class="container--narrow">
              <div class="body-content wysiwyg"
                   :id="secondBodyContentWrapper"
                   data-height=""
                   v-html="modalTextFooter">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button @click="closeModal" v-if="hasCloseButton" class="modal__close-btn u-primary-medium-dark u-hover-primary-medium-dark-2" :data-modal="wrapperId">
      <svg xmlns="http://www.w3.org/2000/svg"
           width="36"
           height="36"
           viewBox="0 0 36 36"
           aria-hidden="true">
        <g fill="none" fill-rule="nonzero">
          <circle cx="18" cy="18" r="18" fill="currentColor"></circle>
          <path fill="#FFFFFF"
                d="M19.806 27.414L19.806 19.806 27.414 19.806 27.414 16.194 19.806 16.194 19.806 8.586 16.194 8.586 16.194 16.194 8.586 16.194 8.586 19.806 16.194 19.806 16.194 27.414z"
                transform="rotate(45 18 18)"></path>
        </g>
      </svg>Close
    </button>
  </div>
</template>

<script>
import moment from 'moment';
import {mapGetters} from "vuex";

export default {
  name: "DelegateModal",
  props: {
    hasCloseButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      authFailed: false,
      authSucceeded: false,
      ariaBusy: false,
      bodyContentWrapper: 'body-content-accordion-1-' + parseInt(Math.random() * 100000),
      email: null,
      failureMessage: '',
      id: null,
      loaded: false,
      modalTextIntro: '',
      modalTextFooter: '',
      secondBodyContentWrapper: 'body-content-accordion-1-' + parseInt(Math.random() * 100000),
      successMessage: '',
      wrapperId: 'modal-wrapper-' + parseInt(Math.random() * 100000),
      modalVisible: this.showModal,
      event: null,
    }
  },
  computed: {
    ...mapGetters({
      raYear: 'getRaYear',
      getNextUrl: 'getNextUrl'
    }),
    delegateLoginId() {
      return 'delegate-login-number-' . this.raYear
    },
  },
  mounted() {
    this.axios.get(`/api/delegate/login-form-text`).then((res) => {
      this.modalTextIntro = res.data.modalTextIntro
      this.modalTextFooter = res.data.modalTextFooter
    }).catch((err) => {
      console.log(err.response.data.message)
    }).finally(() => {
      this.loaded = true
    })

    document.addEventListener('keydown', (e) => {
      const html = document.querySelector('html');

      if (e.code == 'Escape' && html.classList.contains('no-scroll')) {
        this.closeModal();
      }
    });

    document.addEventListener('click', (e) => {
      if (e.defaultPrevented) return;

      const html = document.querySelector('html');

      if (
        !e.target.closest('.modal') &&
        html.classList.contains('no-scroll') &&
        !e.target.closest('.fc-event') &&
        !e.target.closest('button')
      ) {
        this.closeModal();
      }
    });
  },
  methods: {
    closeModal() {
      this.$emit('closeDelegateModal')
    },
    setEvent(e) {
      this.event = e;
    },
    openModal() {
      if (this.event) {
        if (
          this.event.key == ' ' ||
          this.event.key == 'Enter'
        ) {
          // wait for modal to attach to refs
          this.$nextTick(() => {
            // use this class to only show outline only when using keyboard navigation
            this.forcedFocusable
              ? this.forcedFocusable.classList.add('is-keyboard-focused')
              : this.firstFocusable.classList.add('is-keyboard-focused');
          });
        }
      }
    },
    setKeyboardNav() {
      // wait for refs to attach
      this.$nextTick(() => {
        // attach event listeners to first and last focusable items
        const forcedFocusEl = this.$refs.modal.querySelector("[tabindex='-1']");
        const length = this.getFocuseableElements(
          this.$refs.modalwrapper
        ).length;
        const firstFocusEl = this.getFocuseableElements(this.$refs.modal)[0];

        // set first and forced focusable elements
        this.forcedFocusable = forcedFocusEl;
        this.forcedFocusable?.addEventListener(
          'keydown',
          this.handleFirstElementKeydown
        );

        this.firstFocusable = firstFocusEl;
        this.firstFocusable.addEventListener(
          'keydown',
          this.handleFirstElementKeydown
        );

        // set focus
        this.forcedFocusable
          ? this.forcedFocusable.focus()
          : this.firstFocusable.focus();

        this.lastFocusable = this.getFocuseableElements(
          this.$refs.modalwrapper
        )[length - 1];

        this.lastFocusable.addEventListener(
          'keydown',
          this.handleLastElementKeydown
        );
      });
    },
    unsetKeyboardNav() {
      // remove event listeners
      this.forcedFocusable?.removeEventListener(
        'keydown',
        this.handleFirstElementKeydown
      );
      this.firstFocusable.removeEventListener(
        'keydown',
        this.handleFirstElementKeydown
      );
      this.lastFocusable.removeEventListener(
        'keydown',
        this.handleLastElementKeydown
      );
    },
    handleFirstElementKeydown(e) {
      if (e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        this.lastFocusable.focus();
      }

      this.forcedFocusable
        ? this.forcedFocusable.classList.add('is-keyboard-focused')
        : this.firstFocusable.classList.add('is-keyboard-focused');
    },
    handleLastElementKeydown(e) {
      if (!e.shiftKey && e.key === 'Tab') {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    },
    submitForm() {
      this.ariaBusy = true

      const email = this.email
      const id = this.id

      this.axios.get(`/api/delegate/auth`, {
        params: {
          email,
          id,
        },
        headers: {
          'x-token': window.drupalSettings?.ra?.ra_delegate_internal_api_token ?? null
        },
      }).then((res) => {
        this.authFailed = false
        this.failureMessage = ''

        this.successMessage = res.data.message
        this.authSucceeded = true

        // There may be a 'nextUrl' if the user logged in from the Event modal.
        // Redirect them there is it exists. Otherwise, just reload.
        const nextUrl = this.getNextUrl
        if (nextUrl) {
          // Clear out the store since we no longer need or want this.
          this.$store.commit('updateNextUrl', null)
        }

        const wait = setTimeout(() => {
          window.location = nextUrl
            ? nextUrl
            : window.location
        }, 1750)
      }).catch((err) => {
        this.authFailed = true
        this.failureMessage = err.response.data.message

        this.successMessage = ''
        this.authSucceeded = false
        this.ariaBusy = false
      })
    },
    getFocuseableElements(element) {
      return element.querySelectorAll(
        'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
      );
    },
  },
  watch: {
    showModal(newVal) {
      this.modalVisible = newVal;

      // show modal
      if (newVal) {
        this.setKeyboardNav();
        this.openModal();
      } else {
        this.unsetKeyboardNav();
      }
    },
  },
}
</script>

<style scoped>

</style>
